<template>
<div class="erm-borrow-center-list">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="經費預借申請清單(中心)"
      subtitle="檢視各單位經費預借申請進度"
      icon="tabler-cash"
      :count="tbtca61Vos.length"
    />

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="2">
          <v-text-field label="申請單號" v-model="viewParams.filter.borrowApplyNo" clearable prepend-inner-icon="tabler-search"></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            item-title="projectTitle"
            item-value="projectId"
            v-model="viewParams.filter.applyNo"
            :items="viewParams.applyNoSelect"
            label="經費需求項目"
            clearable></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field label="標題" v-model="viewParams.filter.title" clearable prepend-inner-icon="tabler-search"></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            item-title="1"
            item-value="0"
            label="部門"
            :items="viewParams.deptNoSelect" 
            v-model="viewParams.filter.deptNo"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            item-title="1"
            item-value="0"
            label="狀態"
            :items="viewParams.statusSelect" 
            v-model="viewParams.filter.status"
            clearable
          ></v-select>
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :items-length="tbtca61Vos.length"
        :headers="headers"
        :items="pagedItems"
        :loading="loading"
        empty-text="尚無經費預借申請"
        empty-subtitle="可調整上方篩選條件後再查詢"
        empty-icon="tabler-cash-off"
        :items-per-page-options="[
          {value: 10, title: '10'},
          {value: 25, title: '25'},
          {value: 50, title: '50'},
          {value: 100, title: '100'},
        ]"
        :row-props="rowProps"
        :row-clickable="false"
        @row-click="markLastViewed"
      >
        <template v-slot:item.borrowApplyNo="{ item }">
          <div class="d-inline-flex align-center flex-nowrap">
            <v-btn
              @click.stop="goto(item)"
              color="primary"
              variant="text"
              class="pa-0 idt-link font-weight-bold"
            >
              {{ item.borrowApplyNo }}
            </v-btn>
            <v-tooltip location="top" text="複製單號">
              <template #activator="{ props: tipProps }">
                <v-btn
                  v-bind="tipProps"
                  variant="text"
                  icon
                  size="x-small"
                  density="compact"
                  class="ms-1"
                  @click.stop="notifyCopyDocNo(item.borrowApplyNo)"
                >
                  <VIcon icon="tabler-copy" size="18" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.applyNo="{ item }">
          {{ applyNoName(item.applyNo) }}
        </template>
        <template v-slot:item.deptNo="{ item }">
          <div class="d-inline-flex flex-column text-no-wrap">
            <span>{{ viewParams.deptNoMapping[item.deptNo] }}</span>
            <span class="text-caption text-disabled">{{ item.deptNo }}</span>
          </div>
        </template>
        <template v-slot:item.applyTime="{ item }">
          <span v-if="item.applyTime" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
            <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
            {{ convertToROCDate(item.applyTime,'-') }}
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <Status 
            :status="item.status"
            :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
            :is-green="['MApproved']"
            :is-warning="['Draft']"
            :is-secondary="['Passed']"
            shared-table-id="ExpenseBorrow"
            :status-config="getStatusData"
            btn-class="x-large"
            font-class="18px"
          />
        </template>
      </InteractiveDataTable>
  </v-card>
</div>
</template>

<script  setup lang="ts">
import { computed } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
import Status from '../tnm/StatusComponenet.vue'
import { useRouter } from "vue-router"
const router = useRouter()
definePage({
  meta: {
      action: 'manage', //固定為 manage
      subject: 'EXPENSEBORROWCENTERLIST', //系統代碼(系統授權設定中的系統代碼)
  },
});

const viewParams = reactive({
  applyNoSelect: [] ,
  statusSelect: [],
  deptNoSelect: [] ,
  filter: {
    borrowApplyNo: '',
    applyNo: '',
    title: '',
    status: '',
    deptNo:''
  },
  applyNoMapping: {},
  deptNoMapping: {}
});


const tbtca61Vos = reactive([]);
const loading = ref(false)
const page = ref(1)
const itemsPerPage = ref(100)
// 頁面層一次抓回狀態代碼表，傳給所有 Status，避免每列各自打 API 而先顯示英文
const tabId = ref('ExpenseBorrow')
const { data: getStatusData } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value,
  },
}))
// client 端分頁切片（資料一次抓回，前端分頁）
const pagedItems = computed(() => {
  if (itemsPerPage.value === -1) return tbtca61Vos
  const start = (page.value - 1) * itemsPerPage.value

  return tbtca61Vos.slice(start, start + itemsPerPage.value)
})
const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('erm_expense_borrow_center_list_last_clicked', 'borrowApplyNo')

const markLastViewed = (item: any) => {
  setLastClickedId(item?.borrowApplyNo)
}

const headers = [
  { title: "申請單號", key: "borrowApplyNo", sortable: false, icon: "tabler-file-text" },
  { title: "單位", key: "deptNo", sortable: false, icon: "tabler-building" },
  { title: "標題", key: "title", sortable: false, icon: "tabler-clipboard-text" },
  { title: "所屬經費需求計畫", key: "applyNo", sortable: false, icon: "tabler-folder" },
  { title: "申請日期", key: "applyTime", sortable: false, icon: "tabler-calendar-event" },
  { title: "狀態", key: "status", sortable: false, icon: "tabler-flag" }
];


onMounted(() => {
  getSelect();
  search();
  getProjectList();
});

watchDebounced(
  viewParams.filter,
  () => { search(); },
  { debounce: 1000, maxWait: 2000 },
);

const applyNoName = (applyNo) =>{
  return viewParams.applyNoSelect.find((item) => item.projectId==applyNo)?.projectTitle??applyNo
}

const getSelect = async () => {
  const apiUrl='/tca/expenseborrowcenterlist/get/select'
  await $api(apiUrl, {
    method: 'GET'
  }).then((resp) => {

    // Object.assign(viewParams.applyNoSelect, resp.data.applyNoSelect); 
    // 處理ApplyNo映射=applyNoMapping
    // viewParams.applyNoSelect.forEach(select => {
    //   viewParams.applyNoMapping[select[0]] = select[1];
    // });

    Object.assign(viewParams.statusSelect, resp.data.statusSelect); 

    Object.assign(viewParams.deptNoSelect, resp.data.deptNoSelect); 
    // 處理DeptNo映射=deptNoMapping
    viewParams.deptNoSelect.forEach(select => {
      viewParams.deptNoMapping[select[0]] = select[1];
    });

  })
};
const getProjectList = async () => {
  await $api('/tca/expenserequestprojectlist/getAllProjectList', {
    method: 'POST',
    body: {
      deptNo: null,
      isGold: null,
      annual:null, 
    }
  }).then((response) => {
    Object.assign(viewParams.applyNoSelect, response.data); 
    // projectNames.splice(0, projectNames.length, ...response.data)
  }).catch((error) => {
    console.error('取得項目下拉選單失敗:', error);
  });
};

const search = async () => {
  tbtca61Vos.length = 0;
  page.value = 1
  loading.value = true
  const apiUrl='/tca/expenseborrowcenterlist/search'
  await $api(apiUrl, {
    method: 'POST',
    body: viewParams.filter
  }).then((resp) => {
    if (resp.statusCode == 200) {
      // popSwal('success','搜尋成功');
      Object.assign(tbtca61Vos, resp.data); 
    } else  {
      popSwal('error','搜尋失敗,錯誤訊息'+resp.data);
    }
  }).finally(() => {
    loading.value = false
  })
};

const add = () => {
  router.push({path: '/tca/erm/ExpenseBorrow'});
}

const goto = (item) => {
  setLastClickedId(item.borrowApplyNo)
  router.push({ path: '/tca/erm/ExpenseBorrow', query: {
    borrowApplyNo: item.borrowApplyNo, 
  } })
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  $swal({
    icon: ok ? 'success' : 'error',
    title: ok ? '已複製到剪貼簿' : '複製失敗',
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: ok ? 1500 : 2000,
  })
}


const popSwal = (icon,title) => {
  $swal({
    position: 'top-end',
    icon: icon,
    title: title,
    showConfirmButton: false,
    timer: 3000,
    customClass: {
      confirmButton: 'btn btn-primary',
    },
    buttonsStyling: false,
  });
}

</script>

<style scoped>
.ml-2 {
  margin-left: 8px;
}
</style>
